// Modern, icon-only abstract client marks with gradient fills. Always shown in
// full color, with a subtle lift on hover (see .logo-mark in index.css).
// No real company or client names are used anywhere.

const MARKS = [
  // 1. Rounded blade — indigo → violet
  (
    <svg viewBox="0 0 56 56" className="h-16 w-16 md:h-20 md:w-20">
      <defs><linearGradient id="lm1" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#6366f1" /><stop offset="1" stopColor="#a855f7" /></linearGradient></defs>
      <path d="M14 42V22a14 14 0 0 1 28 0v20a2 2 0 0 1-3.3 1.5L28 34l-10.7 9.5A2 2 0 0 1 14 42z" fill="url(#lm1)" />
    </svg>
  ),
  // 2. Interlocking rings — emerald → teal
  (
    <svg viewBox="0 0 56 56" className="h-16 w-16 md:h-20 md:w-20" fill="none">
      <defs><linearGradient id="lm2" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#10b981" /><stop offset="1" stopColor="#0ea5a5" /></linearGradient></defs>
      <circle cx="23" cy="28" r="12" stroke="url(#lm2)" strokeWidth="6" />
      <circle cx="35" cy="28" r="12" stroke="url(#lm2)" strokeWidth="6" opacity="0.55" />
    </svg>
  ),
  // 3. Ascending bars — amber → orange
  (
    <svg viewBox="0 0 56 56" className="h-16 w-16 md:h-20 md:w-20">
      <defs><linearGradient id="lm3" x1="0" y1="1" x2="1" y2="0"><stop offset="0" stopColor="#f59e0b" /><stop offset="1" stopColor="#f97316" /></linearGradient></defs>
      <rect x="13" y="30" width="8" height="14" rx="4" fill="url(#lm3)" />
      <rect x="24" y="22" width="8" height="22" rx="4" fill="url(#lm3)" />
      <rect x="35" y="13" width="8" height="31" rx="4" fill="url(#lm3)" />
    </svg>
  ),
  // 4. Folded chevron — rose → fuchsia
  (
    <svg viewBox="0 0 56 56" className="h-16 w-16 md:h-20 md:w-20" fill="none">
      <defs><linearGradient id="lm4" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#f43f5e" /><stop offset="1" stopColor="#d946ef" /></linearGradient></defs>
      <path d="M16 40L28 14l12 26" stroke="url(#lm4)" strokeWidth="6" strokeLinejoin="round" strokeLinecap="round" />
      <path d="M22 40l6-13 6 13" stroke="url(#lm4)" strokeWidth="6" strokeLinejoin="round" strokeLinecap="round" opacity="0.5" />
    </svg>
  ),
  // 5. Rounded hex — sky → blue
  (
    <svg viewBox="0 0 56 56" className="h-16 w-16 md:h-20 md:w-20">
      <defs><linearGradient id="lm5" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#38bdf8" /><stop offset="1" stopColor="#2563eb" /></linearGradient></defs>
      <path d="M28 10l15 9v18l-15 9-15-9V19z" fill="url(#lm5)" />
      <circle cx="28" cy="28" r="7" fill="#fff" opacity="0.9" />
    </svg>
  ),
  // 6. Pinwheel — violet → indigo
  (
    <svg viewBox="0 0 56 56" className="h-16 w-16 md:h-20 md:w-20">
      <defs><linearGradient id="lm6" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#8b5cf6" /><stop offset="1" stopColor="#6366f1" /></linearGradient></defs>
      <path d="M28 28V10a18 18 0 0 1 18 18z" fill="url(#lm6)" />
      <path d="M28 28h18a18 18 0 0 1-18 18z" fill="url(#lm6)" opacity="0.55" />
      <path d="M28 28V46A18 18 0 0 1 10 28z" fill="url(#lm6)" />
      <path d="M28 28H10A18 18 0 0 1 28 10z" fill="url(#lm6)" opacity="0.55" />
    </svg>
  ),
  // 7. Wave — teal → emerald
  (
    <svg viewBox="0 0 56 56" className="h-16 w-16 md:h-20 md:w-20" fill="none">
      <defs><linearGradient id="lm7" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#14b8a6" /><stop offset="1" stopColor="#22c55e" /></linearGradient></defs>
      <path d="M12 34c5-10 9-10 14 0s9 10 14 0" stroke="url(#lm7)" strokeWidth="6" strokeLinecap="round" />
      <path d="M12 22c5-10 9-10 14 0s9 10 14 0" stroke="url(#lm7)" strokeWidth="6" strokeLinecap="round" opacity="0.5" />
    </svg>
  ),
  // 8. Stacked diamonds — orange → amber
  (
    <svg viewBox="0 0 56 56" className="h-16 w-16 md:h-20 md:w-20">
      <defs><linearGradient id="lm8" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#fb923c" /><stop offset="1" stopColor="#f59e0b" /></linearGradient></defs>
      <rect x="19" y="9" width="18" height="18" rx="5" transform="rotate(45 28 18)" fill="url(#lm8)" />
      <rect x="19" y="29" width="18" height="18" rx="5" transform="rotate(45 28 38)" fill="url(#lm8)" opacity="0.55" />
    </svg>
  ),
];

function Strip() {
  return (
    <div className="flex shrink-0 items-center gap-14 md:gap-20 pr-14 md:pr-20">
      {MARKS.map((mark, i) => (
        <span key={i} className="logo-mark" data-testid={`client-logo-${i}`}>
          {mark}
        </span>
      ))}
    </div>
  );
}

export default function LogoMarquee() {
  return (
    <div className="w-full overflow-hidden py-2 select-none" data-testid="client-logo-marquee">
      <div className="marquee-track">
        <Strip />
        <Strip />
      </div>
    </div>
  );
}
